import type { QueryClient } from '@tanstack/react-query';

import { api } from './http';
import type { TaskDisposition, TaskKind } from './todoist';

export interface BoardUserRef {
  id: number;
  name: string;
  // Task 7 (inactive-queue UI): mirrors the backend's User.active. Optional
  // so existing fixtures/tests that omit it (created before this field
  // existed) stay valid -- `=== false` is the only check that should ever
  // gate UI, never a bare truthiness check on this field.
  active?: boolean;
}

export interface BoardMatterRef {
  id: number;
  title: string;
}

export interface BoardTaskHistory {
  event_type: string;
  actor_name: string | null;
  created_at: string;
}

export interface BoardTaskItem {
  id: number;
  content: string | null;
  description: string | null;
  status: string;
  priority: number;
  due_date: string | null;
  task_kind: TaskKind;
  disposition: TaskDisposition | null;
  matter: BoardMatterRef | null;
  // A8 (Task Center Phase 2): matter_masked distinguishes "no matter"
  // (matter === null, matter_masked === false) from "matter masked"
  // (matter === null, matter_masked === true) — can_edit/can_complete are
  // server-computed mirrors of the existing PATCH/complete/reopen
  // authorization rule so the UI never renders a dead affordance.
  matter_masked: boolean;
  can_edit: boolean;
  can_complete: boolean;
  assigned_to: BoardUserRef | null;
  created_by: BoardUserRef | null;
  mirror_state: 'live' | 'detached' | 'off';
  todoist_task_id: string | null;
  source_system?: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  history: BoardTaskHistory | null;
}

export interface BoardDeadlineItem {
  id: number;
  deadline_type: string;
  title: string;
  deadline_date: string;
  // Cross-view deadline details are omitted unless the viewer can access
  // the matter; matter can still be null for legacy/missing matter rows.
  matter: BoardMatterRef | null;
}

export interface BoardCounts {
  overdue: number;
  today: number;
  upcoming: number;
  no_date: number;
  completed_30d: number;
}

export interface TaskBoardResponse {
  user: BoardUserRef;
  tasks: BoardTaskItem[];
  deadlines: BoardDeadlineItem[];
  counts: BoardCounts;
  requested_task_id: number | null;
  resolved_view: 'active' | 'completed';
}

export interface GetTaskBoardParams {
  user_id: number;
  view?: 'active' | 'completed';
  task_id?: number;
}

export const taskBoardKey = (params: GetTaskBoardParams) =>
  ['task-board', params.user_id, params.view ?? 'active', params.task_id ?? null] as const;

export const getTaskBoard = (params: GetTaskBoardParams) =>
  api.get<TaskBoardResponse>('/tasks/board', { params }).then((r) => r.data);

/** The shared-cache-key invalidation contract (spec §5/§6): every
 * mutation fired from the Task Center page invalidates ITS OWN board
 * query prefix plus the two pre-existing prefixes the hub/Dashboard
 * already read from, so all three surfaces stay in sync exactly as
 * today — used by Tasks 6-8's complete/reopen/edit/create mutations. */
export const invalidateTaskCaches = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['task-board'] });
  queryClient.invalidateQueries({ queryKey: ['my-work'] });
  queryClient.invalidateQueries({ queryKey: ['ai-hub', 'work-items'] });
  queryClient.invalidateQueries({ queryKey: ['ai-hub', 'tasks'] });
};

// PA11: mutations (complete/reopen/update/create/etc.) are imported
// directly from `./todoist` by consumers — this module is not a
// re-export layer for them.

export interface TaskCounts {
  overdue: number;
  today: number;
  upcoming: number;
  no_date: number;
}

/** P3-A3: self-only, tz-aware badge counts — see useAppBadge. */
export const taskCountsKey = (tz: string) => ['task-board-counts', tz] as const;

export const getTaskCounts = (tz: string) =>
  api.get<TaskCounts>('/tasks/board/counts', { params: { tz } }).then((r) => r.data);
